import { useUnloadingPatchMutation } from "api/wms/hooks";
import { UnloadingDetails, UnloadingStatus } from "api/wms/models";
import { Button } from "components/miloDesignSystem/atoms/button";
import { IconButton } from "components/miloDesignSystem/atoms/iconButton";
import { MdiAdd } from "components/miloDesignSystem/atoms/icons/MdiAdd";
import { MdiCheck } from "components/miloDesignSystem/atoms/icons/MdiCheck";
import { MdiDelete } from "components/miloDesignSystem/atoms/icons/MdiDelete";
import { MdiDownloadPdf } from "components/miloDesignSystem/atoms/icons/MdiDownloadPdf";
import { MdiKeyboardTab } from "components/miloDesignSystem/atoms/icons/MdiKeyboardTab";
import { MdiMoreHoriz } from "components/miloDesignSystem/atoms/icons/MdiMoreHoriz";
import { MdiQrCode } from "components/miloDesignSystem/atoms/icons/MdiQrCode";
import { Menu } from "components/miloDesignSystem/atoms/menu";
import { MenuItemType } from "components/miloDesignSystem/atoms/menu/types";
import { Tooltip } from "components/miloDesignSystem/atoms/tooltip";
import { RightPanelHeader } from "components/utils/drawer";
import { useToggle } from "hooks";
import { AddOrdersBySignaturesModal } from "./addOrdersBySignaturesModal/AddOrdersBySignaturesModal";
import { dateFns, getAnyErrorKey } from "utilities";
import { withDeleteConfirmation } from "hooks/withMutationConfirmation";
import { useMutation } from "hooks/useMutation";
import { deleteUnloading } from "api/wms/calls";
import { wmsKeys } from "api/wms/keys";
import { useDownloadUnloadingPdf } from "../../hooks/useDownloadUnloadingPdf";

interface Props {
  close: () => void;
  unloading: UnloadingDetails;
}

export const PanelHeader = ({ close, unloading }: Props) => {
  const startUnloadingMutation = useUnloadingPatchMutation();
  const finishUnloadingMutation = useUnloadingPatchMutation();
  const addOrdersBySignaturesModal = useToggle();
  const handleDownloadUnloadingPdf = useDownloadUnloadingPdf();

  const deleteMutation = withDeleteConfirmation(
    useMutation(deleteUnloading, ({ queryClient, toastr }) => ({
      onSuccess: () => {
        close();
        queryClient.invalidateQueries(wmsKeys.unloads.list());
        toastr.open({
          type: "success",
          title: "Udało się!",
          text: `Usunięto rozładunek "${unloading.signature}"`,
        });
      },
      onError: error => {
        toastr.open({
          type: "warning",
          title: "Wymagane działanie",
          text: getAnyErrorKey(error),
        });
      },
    })),
    "Czy na pewno chcesz usunąć ten rozładunek?",
  )();

  return (
    <>
      <RightPanelHeader>
        {!unloading.startedAt && !unloading.finishedAt && (
          <Button
            className="text-uppercase"
            disabled={startUnloadingMutation.isLoading}
            onClick={() =>
              startUnloadingMutation.mutate({
                id: unloading.id,
                toUpdate: {
                  startedAt: dateFns.format(new Date(), "yyyy-MM-dd HH:mm:ss.SSSSSS"),
                  status: UnloadingStatus.IN_PROGRESS,
                },
              })
            }
            size="small"
            startIcon={MdiCheck}
            theme="light"
            variant="outline"
          >
            Rozpocznij rozładunek
          </Button>
        )}
        {unloading.startedAt && !unloading.finishedAt && (
          <Button
            className="text-uppercase"
            disabled={finishUnloadingMutation.isLoading}
            onClick={() =>
              finishUnloadingMutation.mutate({
                id: unloading.id,
                toUpdate: {
                  finishedAt: dateFns.format(new Date(), "yyyy-MM-dd HH:mm:ss.SSSSSS"),
                  status: UnloadingStatus.FINISHED,
                },
              })
            }
            size="small"
            startIcon={MdiCheck}
            theme="light"
            variant="outline"
          >
            Zakończ rozładunek
          </Button>
        )}
        <div className="d-flex align-items-center justify-content-end flex-1">
          <IconButton
            icon={<MdiQrCode size="16" />}
            onClick={() => {
              handleDownloadUnloadingPdf(unloading);
            }}
            variant="blackT"
          />
          <IconButton icon={<MdiDownloadPdf size="16" />} variant="blackT" />
          <Menu
            disclosure={<IconButton icon={MdiMoreHoriz} variant="blackT" />}
            menuItems={[
              {
                type: MenuItemType.TEXT,
                options: {
                  iconStart: MdiAdd,
                },
                onClick: addOrdersBySignaturesModal.open,
                text: "Dodaj zamówienia do rozładunku",
              },
              {
                type: MenuItemType.TEXT,
                options: {
                  disabled: unloading.status !== UnloadingStatus.NOT_STARTED,
                  color: "red100",
                  iconStart: MdiDelete,
                },
                onClick: () => deleteMutation.mutate(unloading.id),
                text: "Usuń",
              },
            ]}
          />
          <span className="divider line-divider" />
          <Tooltip title="Zamknij panel">
            <IconButton icon={MdiKeyboardTab} onClick={close} variant="blackT" />
          </Tooltip>
        </div>
      </RightPanelHeader>
      {addOrdersBySignaturesModal.isOpen && (
        <AddOrdersBySignaturesModal
          close={addOrdersBySignaturesModal.close}
          unloading={unloading}
        />
      )}
    </>
  );
};
